import React from "react";
import { ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Container, PageHeader, QuoteForm, BRANCHES, PHONE, PHONE_DISPLAY, EMAIL, HOURS, INKS, waLink } from "@/components/brand";

const channels = [
  { label: "Call", value: PHONE_DISPLAY, href: `tel:+${PHONE}`, note: "Fastest for urgent jobs" },
  { label: "WhatsApp", value: PHONE_DISPLAY, href: waLink("Hi, I'd like a quote for a print job."), note: "Send your artwork straight from your phone", external: true },
  { label: "Email", value: EMAIL, href: `mailto:${EMAIL}`, note: "Best for large files and detailed briefs" },
];

export default function ContactPage() {
  return (
    <div data-testid="contact-page" className="min-h-screen bg-paper">
      <Navbar />
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
        title="Talk to the people who print it"
        lede={`Call, message or walk into any branch. We're open ${HOURS}, and we reply to quote requests within one working day.`}
      />

      <main>
        <Container className="py-16 md:py-24 grid gap-14 lg:grid-cols-[1fr_minmax(0,32rem)] lg:gap-20">
          <div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-ink">Reach us directly</h2>
            <ul className="mt-8 border-t border-ink">
              {channels.map((c) => (
                <li key={c.label} className="border-b border-ink">
                  <a href={c.href} {...(c.external ? { target: "_blank", rel: "noreferrer" } : {})}
                    className="group grid grid-cols-[6rem_1fr_auto] sm:grid-cols-[8rem_1fr_auto] items-baseline gap-4 py-6">
                    <span className="font-display font-medium text-graphite">{c.label}</span>
                    <span>
                      <span className="block font-display font-bold text-xl sm:text-2xl text-ink group-hover:text-process-magenta transition-colors break-all sm:break-normal">{c.value}</span>
                      <span className="block mt-1 text-sm text-graphite">{c.note}</span>
                    </span>
                    <ArrowUpRight size={20} className="text-ink group-hover:text-process-magenta transition-colors" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-12 border border-ink">
              <iframe
                title="Map of Print Xpress, Koramangala 7th Block"
                src="https://maps.google.com/maps?q=405,+1st+Main+Rd,+7th+Block,+Koramangala,+Bengaluru+560095&z=15&output=embed"
                width="100%"
                height="300"
                style={{ border: 0, display: "block", filter: "grayscale(1) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div id="quote" className="scroll-mt-24">
            <QuoteForm
              testid="contact-page-form"
              title="Ask for a quote"
              intro="The more detail you give, the more exact the price."
              className="shadow-[8px_8px_0_0_#E5007D]"
            />
          </div>
        </Container>

        <section className="bg-ink text-paper py-16 md:py-24">
          <Container>
            <h2 className="font-display font-bold text-3xl sm:text-5xl">Branches</h2>
            <ul className="mt-10 grid gap-px bg-paper/20 border border-paper/20 md:grid-cols-3">
              {BRANCHES.map((b, i) => (
                <li key={b.name} className="bg-ink p-6 sm:p-8 flex flex-col" data-testid={`cp-location-${i}`}>
                  <span className="w-8 h-8" style={{ backgroundColor: INKS[i] }} aria-hidden="true" />
                  <h3 className="mt-6 font-display font-bold text-2xl">{b.name}</h3>
                  <p className="mt-2 text-paper/70 leading-relaxed">{b.address}</p>
                  <p className="mt-3 text-paper/90">{b.note}</p>
                  <a href={b.map} target="_blank" rel="noopener noreferrer"
                    className="mt-6 self-start inline-flex items-center gap-1 font-display font-semibold hover:text-process-yellow transition-colors">
                    Get directions <ArrowUpRight size={16} aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
}
